import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Send, BellRing, X } from "lucide-react";
import { toast } from "sonner";

export function SignoffRequests({ doc, sector, title, onChanged }) {
  const [emails, setEmails] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(null);
  const base = `/me/policies/${sector}/${encodeURIComponent(title)}/signoff-requests`;
  const pending = doc.signoff_requests.filter((r) => r.status === "pending");
  const signed = doc.signoff_requests.filter((r) => r.status === "signed");

  const send = async (e) => {
    e.preventDefault(); setBusy("send");
    try {
      const list = emails.split(/[\s,;]+/).filter(Boolean);
      const { data } = await api.post(base, { emails: list, message });
      toast.success(`Sign-off request sent to ${data.sent.length}${data.failed.length ? ` · ${data.failed.length} failed` : ""}`);
      setEmails(""); setMessage(""); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send"); } finally { setBusy(null); }
  };
  const remind = async (email) => {
    setBusy(email);
    try { await api.post(`${base}/${encodeURIComponent(email)}/remind`); toast.success(`Reminder sent to ${email}`); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not remind"); } finally { setBusy(null); }
  };
  const cancel = async (email) => { await api.delete(`${base}/${encodeURIComponent(email)}`); onChanged(); };

  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="policy-signoff-requests">
      <p className="font-medium text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-accent" /> Ask staff to sign by email
        {pending.length > 0 && <Badge variant="secondary" data-testid="signoff-pending-count">{pending.length} pending</Badge>}
      </p>
      <form onSubmit={send} className="space-y-2">
        <Textarea rows={2} placeholder="Staff emails — separate with commas or new lines" value={emails} onChange={(e) => setEmails(e.target.value)} required data-testid="signoff-request-emails" />
        <Input placeholder="Optional note (e.g. please sign before Friday's team meeting)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="signoff-request-message" />
        <Button size="sm" type="submit" disabled={busy === "send"} data-testid="signoff-request-send">{busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send read &amp; sign links</>}</Button>
      </form>
      {doc.signoff_requests.length > 0 && (
        <ul className="text-xs space-y-1.5" data-testid="signoff-request-list">
          {pending.map((r) => (
            <li key={`p-${r.email}-${r.created_at}`} className="flex items-center justify-between gap-2" data-testid={`signoff-request-${r.email}`}>
              <span className="truncate">{r.email} <span className="text-muted-foreground">· sent {new Date(r.created_at).toLocaleDateString("en-GB")}{r.reminded_at ? " · reminded" : ""}</span></span>
              <span className="flex gap-1 shrink-0">
                <button type="button" title="Send reminder" disabled={busy === r.email} onClick={() => remind(r.email)} className="text-primary hover:underline" data-testid={`signoff-remind-${r.email}`}><BellRing className="w-3.5 h-3.5" /></button>
                <button type="button" title="Cancel request" onClick={() => cancel(r.email)} className="text-muted-foreground hover:text-destructive" data-testid={`signoff-cancel-${r.email}`}><X className="w-3.5 h-3.5" /></button>
              </span>
            </li>
          ))}
          {signed.map((r) => (
            <li key={`s-${r.email}-${r.created_at}`} className="flex items-center justify-between gap-2 text-emerald-700" data-testid={`signoff-request-${r.email}`}>
              <span className="truncate">{r.email}</span><span className="shrink-0">signed {new Date(r.signed_at).toLocaleDateString("en-GB")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
